import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import type { ListResponse, SpeakerRow, SpeechRow } from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";
import { useSpeakerColumns, useSpeechColumns } from "../components/domainColumns";

/**
 * Sidebar entry "speaker_view" (navigation.speaker_view) → /data/speaker_data.
 * The mirror image of SpeechDataPage: list the speakers, drill down into the
 * speeches each of them offers.
 */
export function SpeakerDataPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<SpeakerRow | null>(null);

  const speakerColumns = useSpeakerColumns();
  const speechColumns = useSpeechColumns();

  const speakers = useQuery({
    queryKey: ["data", "speakers", filters],
    queryFn: () => apiFetch<ListResponse<SpeakerRow>>(withQuery("/data/speakers", filters)),
  });

  const speeches = useQuery({
    queryKey: ["data", "speaker-speeches", selected?.id],
    queryFn: () =>
      apiFetch<ListResponse<SpeechRow>>(`/data/speakers/${selected!.id}/speeches`),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      {
        name: "first_name",
        label: t("activerecord.attributes.speaker.first_name"),
      },
      {
        name: "last_name",
        label: t("activerecord.attributes.speaker.last_name"),
      },
      {
        name: "congregation",
        label: t("activerecord.models.congregation"),
      },
      {
        name: "circumscription",
        label: t("activerecord.models.circumscription"),
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.speaker_view")}>
      <FilterBar fields={fields} onApply={setFilters} loading={speakers.isFetching} />

      <DataTable
        columns={speakerColumns}
        rows={speakers.data?.data}
        exportAs={t("navigation.speaker_view")}
        loading={speakers.isPending}
        error={speakers.error}
        onRowClick={setSelected}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={860}
        destroyOnHidden
        title={selected && [selected.first_name, selected.last_name].filter(Boolean).join(" ")}
      >
        <Typography.Title level={5}>
          {t("activerecord.attributes.speaker.speeches")}
        </Typography.Title>

        {/* Names the speaker for the same reason the other screen names the
            talk: the file has to say whose list it is. */}
        <DataTable
          columns={speechColumns}
          rows={speeches.data?.data}
          exportAs={[selected?.first_name, selected?.last_name, t("activerecord.attributes.speaker.speeches")]
            .filter(Boolean)
            .join(" · ")}
          loading={speeches.isPending}
          error={speeches.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
